'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { FC, useEffect, useRef } from 'react';
import { AwardsDto } from '@/components/Awards/interface';
import TitleGraffiti from '@/components/shared/TitleGraffiti';
import cn from '@/helpers/cn';
import useDevice from '@/hooks/useDevice';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
import Table from './Table';
interface Props {
  data: AwardsDto;
}
const AwardsTable: FC<Props> = t0 => {
  const $ = _c(34);
  const {
    data
  } = t0;
  const wrapperRef = useRef(null);
  const tl = useRef(null);
  const shouldLoadVideos = useLazyVideoSource(wrapperRef);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [wrapperRef];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useViewportVideosPlayback(t1);
  const device = useDevice();
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const wrapper = wrapperRef.current;
      const curvedText = wrapper?.querySelector(`.${css.curvedText}`);
      const curvedWrapper = wrapper?.querySelector(`.${css.curvedWrapper}`);
      if (!wrapper || !curvedText || !curvedWrapper) {
        return;
      }
      const ctx = gsap.context(() => {
        const matchMedia = gsap.matchMedia();
        matchMedia.add({
          isDesktop: "(min-width: 992px)",
          isMobile: "(max-width: 991px)"
        }, context => {
          const {
            isMobile
          } = context.conditions as gsap.Conditions;
          tl.current = gsap.timeline({
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: curvedWrapper,
              start: () => isMobile ? "bottom bottom" : `top-=${window.innerHeight * 0.05} bottom`,
              end: "bottom+=35% top",
              onUpdate: t4 => {
                const {
                  progress
                } = t4;
                const scrollProgress = progress * 360;
                gsap.to(curvedText, {
                  rotate: scrollProgress,
                  scale: 1.05,
                  ease: "none",
                  duration: 0
                });
              }
            }
          });
          if (isMobile) {
            return;
          }
          gsap.to(curvedWrapper, {
            y: "-25%",
            force3D: !isMobile,
            willChange: "transform",
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: curvedWrapper,
              start: "center-=25%",
              end: "bottom",
              markers: false,
              scrub: true
            }
          });
        });
      }, wrapper);
      return () => ctx.revert();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== data.title) {
    t4 = <TitleGraffiti className={css.titleGraffiti} title={data.title} url="/assets/video/mp4/awards-graffity.mp4" />;
    $[3] = data.title;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn(css.video, css.videoLeft);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== shouldLoadVideos) {
    t6 = shouldLoadVideos ? <><source src="/assets/video/webm/about-us-awards-left.webm" type="video/webm" /><source src="/assets/video/mp4/about-us-awards-left.mp4" type="video/mp4" /></> : null;
    $[6] = shouldLoadVideos;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <video loop={true} playsInline={true} muted={true} controls={false} className={t5} preload="none">{t6}</video>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn(css.video, css.videoRight);
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== shouldLoadVideos) {
    t9 = shouldLoadVideos ? <><source src="/assets/video/webm/about-us-awards-right.webm" type="video/webm" /><source src="/assets/video/mp4/about-us-awards-right.mp4" type="video/mp4" /></> : null;
    $[11] = shouldLoadVideos;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <video loop={true} playsInline={true} muted={true} controls={false} className={t8} preload="none">{t9}</video>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className={css.circle} />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== data.awardsItem) {
    t12 = data?.awardsItem ? <Table items={data.awardsItem} /> : null;
    $[16] = data.awardsItem;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <div className={css.tableWrapper}>{t12}</div>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t10 || $[21] !== t13 || $[22] !== t7) {
    t14 = <div className={css.curvedContainer}>{t7}{t10}{t11}{t13}</div>;
    $[20] = t10;
    $[21] = t13;
    $[22] = t7;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = device === "desktop" ? 1775 : 424;
  const t16 = device === "desktop" ? 1775 : 424;
  const t17 = device === "desktop" ? "/assets/images/circle-text-white.webp" : "/assets/images/circle-text-mobile-white.webp";
  let t18;
  if ($[24] !== t15 || $[25] !== t16 || $[26] !== t17) {
    t18 = <div className={css.curvedContainer}><Image width={t15} height={t16} alt="" className={css.curvedText} src={t17} /></div>;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t14 || $[29] !== t18) {
    t19 = <div className={css.content}><div className={css.curvedWrapper}>{t14}{t18}</div></div>;
    $[28] = t14;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t19 || $[32] !== t4) {
    t20 = <section ref={wrapperRef} className={css.wrapper}>{t4}{t19}</section>;
    $[31] = t19;
    $[32] = t4;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  return t20;
};
export default AwardsTable;